import React, { useState, useEffect } from "react";
import "../styles/ReportePagosYPopularidad.css";
import { IP } from '../configuracion.js';

const BASE_API_URL = `${IP}/tavpos/backend`;

const ReporteProductosMasVendidos = ({ property = "propiedad2" }) => {
  const [productos, setProductos] = useState([]);
  const [groupedProductos, setGroupedProductos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiStatus, setApiStatus] = useState({
    reachable: null,
    testResponse: null,
  });
  const [numberFormat, setNumberFormat] = useState('en-US'); // Default fallback
  const [currency, setCurrency] = useState('USD'); // Default fallback

  const buildApiUrl = (endpoint) =>
    `${BASE_API_URL}/${endpoint}?property=${encodeURIComponent(property)}`;

  const checkApiEndpoint = async () => {
    const url = buildApiUrl("lectura_productos_vendidos.php");
    try {
      console.log("Checking API endpoint:", url);
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "X-Property": property,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });
      console.log("checkApiEndpoint response status:", response.status);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      console.log("checkApiEndpoint response data:", data);
      setApiStatus({ reachable: true, testResponse: data });
      return true;
    } catch (error) {
      console.error("checkApiEndpoint error:", error.message);
      setApiStatus({ reachable: false, testResponse: null });
      return false;
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await fetch(buildApiUrl("get_timezone.php"));
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al cargar configuración (${response.status}): ${errorText}`);
      }
      const data = await response.json();
      console.log("Datos de get_timezone.php:", JSON.stringify(data, null, 2));
      if (!data.success || !data.data) {
        throw new Error(data.message || "No se pudo obtener la configuración.");
      }
      setNumberFormat(data.data.numberFormat || 'en-US');
      setCurrency(data.data.currency || 'USD');
      console.log("Configuración establecida - numberFormat:", data.data.numberFormat, "currency:", data.data.currency);
    } catch (error) {
      console.error("Error al cargar configuración:", error);
      setError(error.message || "Error al cargar configuración. Usando en-US y USD como fallback.");
    }
  };

  const groupProductsByDepartment = (products) => {
    const grouped = {};

    products.forEach((product) => {
      const depto = product.departamento || "Sin departamento";
      if (!grouped[depto]) {
        grouped[depto] = {
          totalVentas: 0,
          cantidadTotal: 0,
          productos: [],
        };
      }

      grouped[depto].totalVentas += product.total || 0;
      grouped[depto].cantidadTotal += product.cantidad || 0;
      grouped[depto].productos.push({
        nombre: product.nombre || "Sin nombre",
        cantidad: product.cantidad || 0,
        total: product.total || 0,
        fecha_control: product.fecha_control || "N/A",
      });
    });

    return grouped;
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat(numberFormat, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (date) =>
    date && date !== "N/A"
      ? new Date(date).toLocaleString(numberFormat)
      : "N/A";

  const fetchProductos = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log("Fetching productos...");
      const isApiReachable = await checkApiEndpoint();
      console.log("isApiReachable:", isApiReachable);
      if (!isApiReachable) {
        throw new Error("API no accesible. Verifica tu conexión de red.");
      }

      const productosResponse = await fetch(
        buildApiUrl("lectura_productos_vendidos.php"),
        {
          method: "GET",
          headers: {
            "X-Property": property,
            "Content-Type": "application/json",
          },
          mode: "cors",
        }
      );
      console.log("fetchProductos response status:", productosResponse.status);
      if (!productosResponse.ok)
        throw new Error(`Error ${productosResponse.status}`);

      const productosData = await productosResponse.json();
      console.log("fetchProductos response data:", productosData);
      if (!productosData.success)
        throw new Error(
          productosData.message || "Error en respuesta de productos"
        );

      setProductos(productosData.data || []);
      setGroupedProductos(groupProductsByDepartment(productosData.data || []));
    } catch (err) {
      console.error("fetchProductos error:", err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig().then(() => fetchProductos());
  }, [property]);

  const ApiStatusIndicator = () => (
    <div className={`api-status ${apiStatus.reachable === false ? "error" : ""}`}>
      <p>
        <strong>Estado del API:</strong>{" "}
        {apiStatus.reachable === false ? " ❌ Inaccesible" : " ⌛ Verificando..."}
      </p>
    </div>
  );

  if (loading)
    return (
      <div className="loading-indicator">
        <div className="spinner"></div>
        <p>Cargando productos...</p>
      </div>
    );

  if (error)
    return (
      <div className="error-message">
        <div className="error-header">
          <i className="bi bi-exclamation-triangle"></i>
          <h3>Error al cargar datos</h3>
        </div>
        <p>{error}</p>
        <button className="btn-retry" onClick={fetchProductos}>
          <i className="bi bi-arrow-repeat"></i> Reintentar
        </button>
      </div>
    );

  return (
    <div className="seccion-productos">
      <h3>
        <i className="bi bi-box-seam"></i> Productos Más Vendidos
      </h3>

      {apiStatus.reachable !== true && <ApiStatusIndicator />}

      {Object.keys(groupedProductos).length > 0 ? (
        <div className="departamentos-container">
          {Object.entries(groupedProductos).map(([departamento, datos]) => (
            <div key={departamento} className="departamento-card">
              <div className="departamento-header">
                <h4>
                  <i className="bi bi-shop"></i> {departamento}
                </h4>
                <div className="departamento-totales">
                  <span>
                    <i className="bi bi-box-seam"></i> {datos.cantidadTotal}{" "}
                    unidades
                  </span>
                  <span>
                    <i className="bi bi-cash-stack"></i>{" "}
                    {formatCurrency(datos.totalVentas)}
                  </span>
                </div>
              </div>

              <div className="productos-list">
                {datos.productos
                  .sort((a, b) => b.cantidad - a.cantidad)
                  .slice(0, 5)
                  .map((producto, index) => (
                    <div key={index} className="producto-item">
                      <div className="producto-info">
                        <span className="producto-nombre">{producto.nombre}</span>
                        <div className="producto-detalle">
                          <span>
                            <i className="bi bi-123"></i> {producto.cantidad}
                          </span>
                          <span>
                            <i className="bi bi-currency-dollar"></i>{" "}
                            {formatCurrency(producto.total)}
                          </span>
                          <span>
                            <i className="bi bi-calendar-check"></i>{" "}
                            {formatDate(producto.fecha_control)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-data">
          <i className="bi bi-database-exclamation"></i>
          <p>No hay datos de productos disponibles</p>
        </div>
      )}
    </div>
  );
};

export default ReporteProductosMasVendidos;